import { api } from "@lumberset/api-client";
import { MessageWorkspaceButton } from "@lumberset/support-ui";
import { money } from "@lumberset/takeoff-ui";
import { Button, Container, SectionHeader, Stat } from "@lumberset/ui";
import {
  BellAlert,
  BookOpen,
  CheckCircleSolid,
  ExclamationCircle,
  ReceiptPercent,
} from "@medusajs/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Crumbs } from "../../components/crumbs";
import { overviewQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(overviewQuery),
  component: Overview,
});

const billingRules: { term: string; explanation: string }[] = [
  {
    term: "First takeoff free",
    explanation:
      "Every new workspace gets the trial takeoffs its Billing Plan grants, free. After that, each released takeoff is charged.",
  },
  {
    term: "Included, then credits, then the fee",
    explanation:
      "A released takeoff uses one included in a monthly plan first (those run out at the month's end), then a credit (bought as a pack, granted here, or from a coupon), and only then the Quote Fee. Account credit granted here in dollars pays the fee before the card. Trial takeoffs, credits and account credit are platform credit: they pay LumberSet fees, nothing else.",
  },
  {
    term: "Coupons and agreed fees",
    explanation:
      "A coupon takes a share or an amount off the next fees. A fee agreed for one workspace replaces the Billing Plan's fee for it alone.",
  },
  {
    term: "Change orders",
    explanation:
      "Revised drawings on a released project are charged the lower Change Order Fee. Trials, credits and coupons never apply to it, but account credit does.",
  },
  {
    term: "Charged on release",
    explanation:
      "The charge lands in the Ledger the moment a takeoff reaches the workspace. Corrections, re-pricing and exports afterwards cost nothing.",
  },
];

interface Attention {
  key: string;
  text: string;
  action?: ReactNode;
}

function Overview() {
  const { data } = useSuspenseQuery(overviewQuery);
  const navigate = useNavigate();
  const staffPanel = (label: string) => (
    <Button asChild size="small" variant="secondary">
      <a href={data.staffPanelUrl} target="_blank" rel="noreferrer">
        {label}
      </a>
    </Button>
  );

  const attention: Attention[] = [
    ...(data.reviewQueue > 0
      ? [
          {
            key: "reviews",
            text: `${data.reviewQueue} ${data.reviewQueue === 1 ? "takeoff is" : "takeoffs are"} waiting for a Reviewer.`,
            action: staffPanel("Open the staff panel"),
          },
        ]
      : []),
    ...(data.tickets.unassigned > 0
      ? [
          {
            key: "tickets",
            text: `${data.tickets.unassigned} ${data.tickets.unassigned === 1 ? "ticket has" : "tickets have"} nobody working on ${data.tickets.unassigned === 1 ? "it" : "them"}.`,
            action: (
              <Button asChild size="small" variant="secondary">
                <Link to="/support">Open support</Link>
              </Button>
            ),
          },
        ]
      : []),
    ...(data.failedPayments > 0
      ? [
          {
            key: "payments",
            text: `${data.failedPayments} ${data.failedPayments === 1 ? "payment" : "payments"} did not go through.`,
            action: (
              <Button asChild size="small" variant="secondary">
                <Link to="/ledger">Open the ledger</Link>
              </Button>
            ),
          },
        ]
      : []),
    ...(data.failedRunsThisWeek > 0
      ? [
          {
            key: "runs",
            text: `${data.failedRunsThisWeek} ${data.failedRunsThisWeek === 1 ? "takeoff" : "takeoffs"} failed this week${
              data.failedRunWorkspaces.length > 0
                ? `, in ${data.failedRunWorkspaces.map((workspace) => workspace.name).join(", ")}`
                : ""
            }. Nobody was charged.`,
            // The workspace's page lists its takeoffs with each failure's error; the Staff
            // panel's queue only holds takeoffs waiting for review.
            action:
              data.failedRunWorkspaces.length === 1 && data.failedRunWorkspaces[0] ? (
                <Button asChild size="small" variant="secondary">
                  <Link to="/yards/$id" params={{ id: data.failedRunWorkspaces[0].id }}>
                    Open the workspace
                  </Link>
                </Button>
              ) : (
                <Button asChild size="small" variant="secondary">
                  <Link to="/yards">Open workspaces</Link>
                </Button>
              ),
          },
        ]
      : []),
    ...(data.pendingPriceProposals > 0
      ? [
          {
            key: "proposals",
            text: `${data.pendingPriceProposals} ${data.pendingPriceProposals === 1 ? "price change is" : "price changes are"} waiting for your approval.`,
            action: (
              <Button asChild size="small" variant="secondary">
                <Link to="/reference-prices" search={{ tab: "approvals" }}>
                  Review price changes
                </Link>
              </Button>
            ),
          },
        ]
      : []),
    ...(data.draftLessons > 0
      ? [
          {
            key: "lessons",
            text: `${data.draftLessons} reading ${data.draftLessons === 1 ? "lesson is" : "lessons are"} waiting for your approval. Approved lessons guide every new takeoff.`,
            action: (
              <Button asChild size="small" variant="secondary">
                <Link to="/lessons">Open lessons</Link>
              </Button>
            ),
          },
        ]
      : []),
    ...(data.pricebookReviewed
      ? []
      : [
          {
            key: "pricebook",
            text: "The reference price book has not been reviewed. Builders' budget estimates say so until you mark it reviewed in Settings.",
            action: (
              <Button asChild size="small" variant="secondary">
                <Link to="/reference-prices">Review prices</Link>
              </Button>
            ),
          },
        ]),
    ...(data.setup.payments
      ? []
      : [
          {
            key: "stripe",
            text: "Payments are off. Charges are recorded in the Ledger but no card is charged.",
          },
        ]),
    ...(data.setup.email
      ? []
      : [
          {
            key: "email",
            text: "Email is not connected. Messages go to the test inbox only.",
          },
        ]),
    ...(data.setup.github && data.setup.githubWebhook
      ? []
      : [
          {
            key: "github",
            text: data.setup.github
              ? "GitHub issues are created, but GitHub's replies do not come back yet: the webhook secret is missing."
              : "GitHub is not connected. Bug reports and feature requests stay in Support until a GitHub token is added.",
          },
        ]),
    ...(data.setup.helpAssistant
      ? []
      : [
          {
            key: "assistant",
            text: "The Help Assistant is off. People can still search articles and open tickets.",
          },
        ]),
  ];

  return (
    <>
      <Crumbs trail={[{ label: "Overview" }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Needs your attention"
          description="What to look at today. Each item disappears when it is dealt with."
        />
        {attention.length === 0 ? (
          <div className="flex items-center gap-x-3 px-6 py-5">
            <span className="text-ui-tag-green-icon">
              <CheckCircleSolid />
            </span>
            <p className="txt-compact-small text-ui-fg-subtle">
              All clear. Nothing needs you right now.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-ui-border-base">
            {attention.map((item) => (
              <li key={item.key} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3">
                <span className="text-ui-tag-orange-icon">
                  <ExclamationCircle />
                </span>
                <p className="txt-compact-small min-w-0 flex-1 text-ui-fg-base">{item.text}</p>
                {item.action}
              </li>
            ))}
          </ul>
        )}
      </Container>

      <Container className="grid grid-cols-2 gap-px overflow-hidden bg-ui-border-base p-0 lg:grid-cols-4">
        <Stat
          className="bg-ui-bg-base"
          label="Workspaces"
          value={data.yards}
          hint={`${data.workspaces.yards} yards, ${data.workspaces.builders} builders, ${data.workspaces.newThisWeek} new this week`}
        />
        <Stat
          className="bg-ui-bg-base"
          label="Takeoffs released"
          value={data.billableQuotesLast30Days}
          hint={`${data.runsLast30Days} started, last 30 days`}
        />
        <Stat
          className="bg-ui-bg-base"
          label="Billed"
          value={money(data.billedCentsLast30Days)}
          hint="Paid and pending, last 30 days"
        />
        <Stat
          className="bg-ui-bg-base"
          label="Open tickets"
          value={data.tickets.open}
          hint={
            <Link to="/support" className="text-ui-fg-interactive hover:underline">
              Open support
            </Link>
          }
        />
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Common tasks"
          description="The things Admins do most, one click away."
        />
        <div className="flex flex-wrap gap-2 px-6 py-4">
          <MessageWorkspaceButton
            client={api}
            onSent={(id) => navigate({ to: "/support/tickets/$id", params: { id } })}
          />
          <Button asChild size="small" variant="secondary">
            <Link to="/announcements">
              <BellAlert /> Send an announcement
            </Link>
          </Button>
          <Button asChild size="small" variant="secondary">
            <Link to="/coupons">
              <ReceiptPercent /> Create a coupon
            </Link>
          </Button>
          <Button asChild size="small" variant="secondary">
            <Link to="/help/articles/new">
              <BookOpen /> Write a help article
            </Link>
          </Button>
        </div>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="How billing works"
          description="The rules every Ledger entry follows."
          actions={
            <Button asChild size="small" variant="secondary">
              <Link to="/billing-plans">Billing plans</Link>
            </Button>
          }
        />
        <dl className="grid gap-x-8 gap-y-5 px-6 py-5 sm:grid-cols-2">
          {billingRules.map((rule) => (
            <div key={rule.term}>
              <dt className="txt-compact-small-plus text-ui-fg-base">{rule.term}</dt>
              <dd className="txt-small mt-1 text-ui-fg-subtle">{rule.explanation}</dd>
            </div>
          ))}
        </dl>
      </Container>
    </>
  );
}
